import { useState, useEffect, useRef } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import Hero from './pricing/hero';
import PlanCards from './pricing/plan-cards';
import Security from './pricing/security';
import Guarantee from './pricing/guarantee';
import Faq from './pricing/faq';
import { loadCheckoutScript, fetchPricingData, getAvailableCycles } from './pricing/helpers';
import { trackBeforeCheckout } from '../helper/utm-tracker';

const Pricing = () => {
    const config = window['JkitDashboardOption']?.freemius?.pricing;

    const [pricingData, setPricingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [billingCycle, setBillingCycle] = useState('annual');
    const [checkingOut, setCheckingOut] = useState(null);
    const [openFaq, setOpenFaq] = useState(0);
    const fsHandlerRef = useRef(null);

    const currency = config?.currency || 'usd';

    useEffect(() => {
        if (!config) { setLoading(false); return; }

        fetchPricingData(config)
            .then((data) => {
                setPricingData(data);
                if (data.plans) {
                    const cycles = getAvailableCycles(data.plans);
                    if (cycles.length > 0) setBillingCycle(cycles[0]);
                }
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCheckout = async (plan, pricingEntry) => {
        setCheckingOut(pricingEntry.id);
        await loadCheckoutScript(config.is_production);

        if (!window.FS || !window.FS.Checkout) {
            setCheckingOut(null);
            return;
        }

        if (!fsHandlerRef.current) {
            fsHandlerRef.current = window.FS.Checkout.configure({
                plugin_id: config.plugin_id,
                public_key: config.plugin_public_key,
                ...(config.sandbox && { sandbox: config.sandbox }),
                ...(config.s_ctx_type && { s_ctx_type: config.s_ctx_type }),
                ...(config.s_ctx_id && { s_ctx_id: config.s_ctx_id }),
                ...(config.s_ctx_ts && { s_ctx_ts: config.s_ctx_ts }),
                ...(config.s_ctx_secure && { s_ctx_secure: config.s_ctx_secure }),
                ...(config.home_url && { home_url: config.home_url }),
                ...(config.next && { next: config.next }),
                ...(config.is_network_admin && { is_network_admin: config.is_network_admin }),
                ...(config.plugin_version && { plugin_version: config.plugin_version }),
            });
        }

        const pricingPlan = window['JkitDashboardOption']?.pricingPlan;
        const resolvedCoupon = (() => {
            if (!pricingPlan) return config.default_coupon || null;
            const id = String(pricingEntry.id);
            for (const key of ['basic', 'professional', 'agency']) {
                const p = pricingPlan[key];
                if (p?.pricing_id && id === String(p.pricing_id) && p.coupon) return p.coupon;
            }
            return config.default_coupon || null;
        })();

        // Save tracker_id + UTM to our server first, then pass tracker_id to Freemius.
        // Freemius webhook will send back tracker_id so server can match UTM data.
        const trackerId = trackBeforeCheckout();

        fsHandlerRef.current.open({
            name: plan.title || plan.name,
            licenses: pricingEntry.licenses || 1,
            billing_cycle: billingCycle,
            currency,
            pricing_id: pricingEntry.id,
            ...(resolvedCoupon && { coupon: resolvedCoupon }),
            tracker_id: trackerId,
        });

        setCheckingOut(null);
    };

    // ── Guard states ─────────────────────────────────────────────────────────

    if (!config) {
        return <div className="f-pricing"><p>{__('Freemius is not available.', 'jeg-elementor-kit')}</p></div>;
    }

    if (loading) {
        return (
            <div className="f-pricing f-pricing--loading">
                <p>{__('Loading pricing data...', 'jeg-elementor-kit')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="f-pricing">
                <p>{sprintf(__('Error: %s', 'jeg-elementor-kit'), error)}</p>
            </div>
        );
    }

    if (!pricingData || !pricingData.plans) {
        return <div className="f-pricing"><p>{__('No pricing data available.', 'jeg-elementor-kit')}</p></div>;
    }

    const visiblePlans = pricingData.plans.filter((p) => !p.is_hidden);
    const availableCycles = getAvailableCycles(visiblePlans);
    const pricingPlan = window['JkitDashboardOption']?.pricingPlan;

    // Server already decided: pricingPlan.is_event_sales === true means event is live.
    const isEventActive = !!pricingPlan?.is_event_sales;
    const activePlan = pricingPlan;

    return (
        <div className={`f-pricing${isEventActive ? ' is-event-sales' : ''}`}>
            <Hero isEventActive={isEventActive} eventExpired={pricingPlan?.event_expired} />

            <PlanCards
                isEventActive={isEventActive}
                availableCycles={availableCycles}
                billingCycle={billingCycle}
                onCycleChange={setBillingCycle}
                visiblePlans={visiblePlans}
                currency={currency}
                activePlan={activePlan}
                checkingOut={checkingOut}
                onCheckout={handleCheckout}
            />

            <Security />
            <Guarantee />
            <Faq openFaq={openFaq} setOpenFaq={setOpenFaq} />
        </div>
    );
};

export default Pricing;
